'use client';

import Image from 'next/image';
import Link from 'next/link';

import BiodermaLogo from '@/fo/icons/base/logos/bioderma.svg';
import EsthedermLogo from '@/fo/icons/base/logos/esthederm.svg';
import EtatPurLogo from '@/fo/icons/base/logos/etat-pur.svg';
import {
  UiKitBreadcrumbsBase,
  UiKitBreadcrumbsBaseProps
} from '@/fo/ui/kit/breadcrumbs/base';
import { useMediaQuery } from '@/fo/ui/mod/media/query';
import { utilScrollToElement } from '@/fo/util/scroll-to-element';

import { styles } from './UiKitBannerAboutBrand.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

const logoMap = {
  bioderma: BiodermaLogo,
  etatPur: EtatPurLogo,
  esthederm: EsthedermLogo
};

type Links = { label: string; href: string; src: string; anchor: boolean };
type Video = {
  desktop: string;
  mobile: string;
};

export type BannerAbout = {
  slug: 'bioderma' | 'etatPur' | 'esthederm';
  video: Video;
  links: Links[];
};

export interface UiKitBannerAboutBrandProps {
  breadcrumbs?: UiKitBreadcrumbsBaseProps;
  bannerAbout: BannerAbout;
  onClick?: NativeElement['onClick'];
}

export function UiKitBannerAboutBrand({
  breadcrumbs,
  bannerAbout,
  ...props
}: UiKitBannerAboutBrandProps) {
  const slots = styles();
  const BrandLogo = logoMap[bannerAbout.slug];

  const isMobile = useMediaQuery('md', 'max');

  const brandColorClasses = {
    bioderma: 'text-brand-bioderma',
    etatPur: 'text-black',
    esthederm: 'text-black'
  }[bannerAbout.slug];

  const scrollToSection = (id: string) => {
    const section = document.getElementById(id);

    utilScrollToElement(section);
  };

  return (
    <div
      className={slots.base()}
      {...props}
    >
      <video
        className={slots.video()}
        autoPlay
        muted
        loop
        playsInline
        width={1440}
        height={400}
        src={isMobile ? bannerAbout.video.mobile : bannerAbout.video.desktop}
      />
      <div className={slots.wrap()}>
        <div className={slots.breadcrumbsWrapper()}>
          {!!breadcrumbs && (
            <UiKitBreadcrumbsBase
              items={breadcrumbs.items}
              className={slots.breadcrumbs()}
            />
          )}
        </div>
        <BrandLogo
          width={352}
          height={80}
          className={slots.brandLogo({ className: brandColorClasses })}
        />
        <div className={slots.linkWrap()}>
          {bannerAbout.links.map((link, index) => (
            <div
              className={slots.linkBlock()}
              key={index}
            >
              {!link.anchor && (
                <Link
                  href={link.href}
                  className={slots.link()}
                >
                  <Image
                    src={link.src}
                    width={80}
                    height={80}
                    alt={link.label}
                    className={slots.linkImage()}
                  />
                  <span className={slots.linkLabel()}>{link.label}</span>
                </Link>
              )}
              {link.anchor && (
                <button
                  onClick={() => scrollToSection(link.href)}
                  className={slots.link()}
                >
                  <Image
                    src={link.src}
                    width={80}
                    height={80}
                    alt={link.label}
                    className={slots.linkImage()}
                  />
                  <span className={slots.linkLabel()}>{link.label}</span>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
